import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import "@/App.css";
import { AuthProvider, useAuth } from "@/context/AuthContext";

import PublicLayout from "@/components/layout/PublicLayout";
import DashboardLayout from "@/components/layout/DashboardLayout";

import Home from "@/pages/Home";
import About from "@/pages/About";
import ForCompanies from "@/pages/ForCompanies";
import ForProfessionals from "@/pages/ForProfessionals";
import ClusterTeamsPage from "@/pages/ClusterTeamsPage";
import Pricing from "@/pages/Pricing";
import Contact from "@/pages/Contact";
import Login from "@/pages/Login";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import BadgeVerify from "@/pages/BadgeVerify";
import EliteProtectionNetwork from "@/pages/EliteProtectionNetwork";
import Trust from "@/pages/Trust";
import PublicTeams from "@/pages/PublicTeams";
import FAQ from "@/pages/FAQ";
import ReliabilityPolicy from "@/pages/ReliabilityPolicy";
import ReferenceConfirm from "@/pages/ReferenceConfirm";
import NegotiationRoom from "@/pages/NegotiationRoom";
import OpsRoom from "@/pages/OpsRoom";
import Cover from "@/pages/Cover";
import AdminNegotiations from "@/pages/admin/AdminNegotiations";
import MyNegotiations from "@/pages/MyNegotiations";
import RegisterStaff from "@/pages/RegisterStaff";
import RegisterCompany from "@/pages/RegisterCompany";

import StaffDashboard from "@/pages/staff/StaffDashboard";
import StaffProfile from "@/pages/staff/StaffProfile";
import StaffJobs from "@/pages/staff/StaffJobs";
import StaffTeams from "@/pages/staff/StaffTeams";
import StaffBookings from "@/pages/staff/StaffBookings";

import CompanyDashboard from "@/pages/company/CompanyDashboard";
import CompanySearch from "@/pages/company/CompanySearch";
import CompanyProfileDetail from "@/pages/company/CompanyProfileDetail";
import CompanyBookings from "@/pages/company/CompanyBookings";
import CompanyBookingDetail from "@/pages/company/CompanyBookingDetail";
import CompanyTeams from "@/pages/company/CompanyTeams";
import CompanyReviews from "@/pages/company/CompanyReviews";
import CompanyJobs from "@/pages/company/CompanyJobs";
import JobDetail from "@/pages/JobDetail";

import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminStaff from "@/pages/admin/AdminStaff";
import AdminCompanies from "@/pages/admin/AdminCompanies";
import AdminBookings from "@/pages/admin/AdminBookings";
import AdminJobs from "@/pages/admin/AdminJobs";
import AdminQA from "@/pages/admin/AdminQA";

import ControlRoom from "@/pages/ops/ControlRoom";
import OpsManager from "@/pages/ops/OpsManager";
import GuardMyJob from "@/pages/ops/GuardMyJob";
import AssignOpsRoles from "@/pages/ops/AssignOpsRoles";
import AdminCpoVetting from "@/pages/admin/AdminCpoVetting";
import AdminManagedDeployments from "@/pages/admin/AdminManagedDeployments";
import AdminMdrDetail from "@/pages/admin/AdminMdrDetail";
import CompanyDeploymentCentre from "@/pages/company/CompanyDeploymentCentre";
import CompanyDeploymentDetail from "@/pages/company/CompanyDeploymentDetail";

function Protected({ roles, children }) {
  const { user, loading } = useAuth();
  if (loading || user === null) return <div className="p-12 text-center text-slate-500">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/" replace />;
  return children;
}

function NegotiationLayoutWrapper() {
  const { user } = useAuth();
  const role = user?.role === "company" ? "company"
    : user?.role === "super_admin" ? "admin"
    : "staff";
  return <DashboardLayout role={role} />;
}

function RootRedirect() {
  const { user, loading } = useAuth();
  if (loading) return <div className="p-12 text-center text-slate-500">Loading…</div>;
  if (user && user.role === "super_admin") return <Navigate to="/admin" replace />;
  if (user && user.role === "company") return <Navigate to="/company" replace />;
  if (user && (user.role === "security_professional" || user.role === "lead_professional")) return <Navigate to="/staff" replace />;
  return null;
}

// Gate the main marketing pages behind auth — Cover shown to non-authenticated visitors.
function GatedHome({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Cover />;
  return children;
}

// Root splash: standalone Cover (no PublicLayout wrapper) for non-auth visitors.
function RootSplash() {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user) return <Navigate to="/home" replace />;
  return <Cover />;
}

export default function App() {
  return (
    <AuthProvider>
      <Toaster position="top-right" richColors />
      <BrowserRouter>
        <Routes>
          {/* Standalone root splash — no PublicLayout for non-auth visitors */}
          <Route path="/" element={<RootSplash />} />

          <Route element={<PublicLayout />}>
            <Route path="/home" element={<GatedHome><Home /></GatedHome>} />
            <Route path="/about" element={<About />} />
            <Route path="/employers" element={<ForCompanies />} />
            <Route path="/security-officers" element={<ForProfessionals />} />
            {/* Legacy URLs — redirect to new segmented paths */}
            <Route path="/for-companies" element={<Navigate to="/employers" replace />} />
            <Route path="/for-professionals" element={<Navigate to="/security-officers" replace />} />
            <Route path="/cluster-teams" element={<ClusterTeamsPage />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/login" element={<Login />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/verify-badge" element={<BadgeVerify />} />
            <Route path="/trust" element={<Trust />} />
            <Route path="/teams" element={<PublicTeams />} />
            <Route path="/faq" element={<FAQ />} />
            <Route path="/reliability-policy" element={<ReliabilityPolicy />} />
            <Route path="/reference-confirm" element={<ReferenceConfirm />} />
            <Route path="/register/staff" element={<RegisterStaff />} />
            <Route path="/register/company" element={<RegisterCompany />} />
            <Route path="/dashboard" element={<RootRedirect />} />
          </Route>

          {/* Standalone public routes (no PublicLayout wrapper) */}
          <Route path="/elite-protection-network" element={<EliteProtectionNetwork />} />

          {/* Staff area */}
          <Route element={<Protected roles={["security_professional", "lead_professional"]}><DashboardLayout role="staff" /></Protected>}>
            <Route path="/staff" element={<StaffDashboard />} />
            <Route path="/staff/profile" element={<StaffProfile />} />
            <Route path="/staff/jobs" element={<StaffJobs />} />
            <Route path="/staff/teams" element={<StaffTeams />} />
            <Route path="/staff/bookings" element={<StaffBookings />} />
            <Route path="/staff/negotiations" element={<MyNegotiations />} />
            <Route path="/staff/my-job" element={<GuardMyJob />} />
          </Route>

          {/* Company area */}
          <Route element={<Protected roles={["company"]}><DashboardLayout role="company" /></Protected>}>
            <Route path="/company" element={<CompanyDashboard />} />
            <Route path="/company/search" element={<CompanySearch />} />
            <Route path="/company/professionals/:ssId" element={<CompanyProfileDetail />} />
            <Route path="/company/bookings" element={<CompanyBookings />} />
            <Route path="/company/bookings/:id" element={<CompanyBookingDetail />} />
            <Route path="/company/teams" element={<CompanyTeams />} />
            <Route path="/company/jobs" element={<CompanyJobs />} />
            <Route path="/company/jobs/:jobId" element={<JobDetail />} />
            <Route path="/company/reviews" element={<CompanyReviews />} />
            <Route path="/company/negotiations" element={<MyNegotiations />} />
            <Route path="/company/control-room" element={<ControlRoom />} />
            <Route path="/company/ops-manager" element={<OpsManager />} />
            <Route path="/company/bookings/:id/assign-roles" element={<AssignOpsRoles />} />
            <Route path="/company/deployment-centre" element={<CompanyDeploymentCentre />} />
            <Route path="/company/deployment-centre/:id" element={<CompanyDeploymentDetail />} />
          </Route>

          {/* Admin area */}
          <Route element={<Protected roles={["super_admin"]}><DashboardLayout role="admin" /></Protected>}>
            <Route path="/admin" element={<AdminDashboard />} />
            <Route path="/admin/staff" element={<AdminStaff />} />
            <Route path="/admin/companies" element={<AdminCompanies />} />
            <Route path="/admin/bookings" element={<AdminBookings />} />
            <Route path="/admin/jobs" element={<AdminJobs />} />
            <Route path="/admin/jobs/:jobId" element={<JobDetail />} />
            <Route path="/admin/qa" element={<AdminQA />} />
            <Route path="/admin/negotiations" element={<AdminNegotiations />} />
            <Route path="/admin/control-room" element={<ControlRoom />} />
            <Route path="/admin/cpo-vetting/:userId" element={<AdminCpoVetting />} />
            <Route path="/admin/managed-deployments" element={<AdminManagedDeployments />} />
            <Route path="/admin/managed-deployments/:id" element={<AdminMdrDetail />} />
          </Route>

          {/* Negotiation room — auth required; sidebar picks the user's own dashboard */}
          <Route element={<Protected roles={["super_admin","company","security_professional","lead_professional"]}><NegotiationLayoutWrapper /></Protected>}>
            <Route path="/negotiations/:id" element={<NegotiationRoom />} />
            <Route path="/ops-room/:bookingId" element={<OpsRoom />} />
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}
